// Enhanced Storage Helper Functions with localStorage persistence
let invoiceStorage = [];
let storageIdCounter = 1;

// Storage keys
const STORAGE_KEYS = {
  INVOICES: 'invoice_generator_invoices',
  COUNTER: 'invoice_generator_counter'
};

// Initialize storage from localStorage on module load
const initializeStorage = () => {
  try {
    // Load invoices from localStorage
    const savedInvoices = localStorage.getItem(STORAGE_KEYS.INVOICES);
    if (savedInvoices) {
      invoiceStorage = JSON.parse(savedInvoices);
      console.log(`Loaded ${invoiceStorage.length} invoices from localStorage`);
    }

    // Load counter from localStorage
    const savedCounter = localStorage.getItem(STORAGE_KEYS.COUNTER);
    if (savedCounter) {
      storageIdCounter = parseInt(savedCounter, 10);
    }
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    // Fallback to empty storage
    invoiceStorage = [];
    storageIdCounter = 1;
  }
};

// Save to localStorage with error handling
const saveToLocalStorage = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.INVOICES, JSON.stringify(invoiceStorage));
    localStorage.setItem(STORAGE_KEYS.COUNTER, storageIdCounter.toString());
    return true;
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
    
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      alert('Storage quota exceeded. Please delete some old invoices.');
    }
    return false;
  }
};

// Save invoice with persistence
export const saveInvoiceToMemory = (invoice) => {
  const invoiceWithId = {
    ...invoice,
    id: invoice.id || storageIdCounter++,
    lastModified: new Date().toISOString(),
    createdAt: invoice.createdAt || new Date().toISOString(),
    version: '1.0' // For future compatibility
  };
  
  const existingIndex = invoiceStorage.findIndex(inv => inv.id === invoiceWithId.id);
  
  if (existingIndex >= 0) {
    // Update existing invoice
    invoiceStorage[existingIndex] = invoiceWithId;
    console.log(`Updated invoice #${invoiceWithId.id}`);
  } else {
    // Add new invoice
    invoiceStorage.push(invoiceWithId);
    console.log(`Saved new invoice #${invoiceWithId.id}`);
  }
  
  // Persist to localStorage
  const saved = saveToLocalStorage();
  
  if (!saved) {
    console.warn('Invoice saved to memory but not localStorage');
  }
  
  return invoiceWithId;
};

// Load invoices from memory/localStorage
export const loadInvoicesFromMemory = () => {
  // Ensure we have the latest data from localStorage
  initializeStorage();
  
  // Return a copy to prevent direct mutation
  return [...invoiceStorage];
};

// Delete invoice with persistence
export const deleteInvoiceFromMemory = (invoiceId) => {
  const initialLength = invoiceStorage.length;
  invoiceStorage = invoiceStorage.filter(inv => inv.id !== invoiceId);
  
  if (invoiceStorage.length < initialLength) {
    console.log(`Deleted invoice #${invoiceId}`);
    saveToLocalStorage();
    return true;
  } else {
    console.warn(`Invoice #${invoiceId} not found`);
    return false;
  }
};

// Get invoice by ID
export const getInvoiceById = (invoiceId) => {
  return invoiceStorage.find(inv => inv.id === invoiceId);
};

// Clear all invoices (with confirmation)
export const clearAllInvoices = () => {
  if (window.confirm('Are you sure you want to delete ALL invoices? This cannot be undone.')) {
    invoiceStorage = [];
    storageIdCounter = 1;
    
    try {
      localStorage.removeItem(STORAGE_KEYS.INVOICES);
      localStorage.removeItem(STORAGE_KEYS.COUNTER);
      console.log('All invoices cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
      return false;
    }
  }
  return false;
};

// Export data for backup
export const exportInvoicesData = () => {
  try {
    const data = {
      invoices: invoiceStorage,
      counter: storageIdCounter,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    // Create download link
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Export failed:', error);
    return false;
  }
};

// Import data from backup
export const importInvoicesData = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Validate data structure
        if (!data.invoices || !Array.isArray(data.invoices)) {
          throw new Error('Invalid backup file format');
        }
        
        // Merge with existing data (or replace)
        const shouldReplace = window.confirm(
          `Import ${data.invoices.length} invoices? This will replace your current invoices.`
        );
        
        if (shouldReplace) {
          invoiceStorage = data.invoices;
          storageIdCounter = data.counter || Math.max(...data.invoices.map(inv => inv.id), 0) + 1;
          
          saveToLocalStorage();
          resolve(data.invoices.length);
        } else {
          resolve(0);
        }
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// Storage info for debugging
export const getStorageInfo = () => {
  const storageSize = new Blob([JSON.stringify(invoiceStorage)]).size;
  
  return {
    invoiceCount: invoiceStorage.length,
    storageSize: `${(storageSize / 1024).toFixed(2)} KB`,
    lastId: storageIdCounter - 1,
    hasLocalStorage: typeof(Storage) !== "undefined",
    localStorageAvailable: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    })()
  };
};

// Auto-save notification component
export const showSaveNotification = (message, type = 'success') => {
  const notification = document.createElement('div');
  notification.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg text-white transition-all duration-300 ${
    type === 'success' ? 'bg-green-500' : 'bg-red-500'
  }`;
  notification.textContent = message;
  
  document.body.appendChild(notification);
  
  // Auto-remove after 3 seconds
  setTimeout(() => {
    notification.style.opacity = '0';
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, 3000);
};

// Initialize storage when module loads
initializeStorage();

// Future Firebase integration placeholders
export const saveToFirebase = async (invoice) => {
  // TODO: Implement Firebase save in version 2
  console.log('Firebase save will be implemented in version 2');
  return { success: false, message: 'Firebase not yet implemented' };
};

export const loadFromFirebase = async () => {
  // TODO: Implement Firebase load in version 2
  console.log('Firebase load will be implemented in version 2');
  return { success: false, data: [] };
};

// Export storage info for debugging
if (typeof window !== 'undefined') {
  window.invoiceStorageDebug = {
    getInfo: getStorageInfo,
    export: exportInvoicesData,
    clear: clearAllInvoices,
    storage: () => invoiceStorage
  };
}